import React, { useState, useEffect } from 'react';
import { Heart } from 'lucide-react';
import ImageCarousel from '../ui/ImageCarousel';

interface HeroSectionProps {
  onStartClick: () => void;
  onLoginClick: () => void;
  userCount: number | null;
}

const carouselImages = [
  '/unnamed_(1).jpg',
  '/unnamed_(2).jpg',
  '/unnamed_(3).jpg',
  '/unnamed_(4).jpg',
  '/unnamed_(5).jpg',
  '/unnamed_(8).jpg',
];

const familyMembers = [
  { word: 'madre', fontClass: 'font-hand-madre' },
  { word: 'padre', fontClass: 'font-hand-padre' },
  { word: 'abuela', fontClass: 'font-hand-abuela' },
  { word: 'abuelo', fontClass: 'font-hand-abuelo' },
];

const HeroSection: React.FC<HeroSectionProps> = ({ onStartClick, onLoginClick, userCount }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % familyMembers.length);
    }, 1300);
    return () => clearInterval(interval);
  }, []);

  const current = familyMembers[currentIndex];

  return (
    <section className="relative pt-24 pb-6 lg:py-0 overflow-hidden">
      <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/3 w-[600px] h-[600px] bg-rose-100 rounded-full blur-3xl opacity-50 -z-10"></div>
      <div className="absolute bottom-0 left-0 translate-y-1/3 -translate-x-1/4 w-[500px] h-[500px] bg-stone-200 rounded-full blur-3xl opacity-50 -z-10 hidden lg:block"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 lg:min-h-[calc(100svh-80px)] lg:flex lg:items-center lg:py-8">
        <div className="flex flex-col lg:flex-row items-center gap-6 lg:gap-12 w-full">
          <div className="lg:w-[65%] space-y-6 text-center lg:text-left">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display italic leading-[1.15] text-stone-900 tracking-tight max-w-3xl">
              Las preguntas que siempre has querido hacerle a{' '}
              <span className="whitespace-nowrap">
                tu{' '}
                <span className="relative inline-block align-bottom pr-3">
                  <span className="invisible font-hand-padre not-italic select-none" aria-hidden="true">padre</span>
                  <span className={`text-rose-500 ${current.fontClass} not-italic absolute left-0 bottom-0`}>
                    {current.word}
                  </span>
                </span>.
              </span>
            </h1>
            <p className="text-lg md:text-xl lg:text-xl text-stone-600 leading-relaxed max-w-2xl mx-auto lg:mx-0">
              Las historias de tus seres queridos guardas para ti, para los hijos de tus hijos y para siempre.
            </p>

            {/* Desktop CTA */}
            <div className="hidden lg:block">
              <button
                onClick={onStartClick}
                className="bg-rose-500 text-white px-8 py-3.5 rounded-full text-base font-semibold hover:bg-rose-600 transition shadow-lg hover:shadow-rose-500/30"
              >
                Empezar
              </button>
            </div>

            {/* Mobile CTAs */}
            <div className="flex flex-col gap-3 w-full max-w-xs mx-auto lg:hidden">
              <button
                onClick={onStartClick}
                className="bg-rose-500 text-white px-8 py-3.5 rounded-full text-base font-semibold hover:bg-rose-600 transition shadow-lg hover:shadow-rose-500/30 w-full"
              >
                Empezar
              </button>
              <button
                onClick={onLoginClick}
                className="text-stone-700 border-2 border-stone-300 px-8 py-3 rounded-full text-base hover:bg-stone-50 transition w-full"
              >
                Iniciar sesión
              </button>
            </div>

            {/* Social proof — heartbeat pill */}
            {userCount !== null && userCount > 0 && (
              <div className="flex justify-center lg:justify-start">
                <div className="inline-flex items-center gap-3 bg-rose-50/80 backdrop-blur-sm px-4 py-1 rounded-full border border-rose-100/50">
                  <Heart size={18} className="text-rose-500 animate-heartbeat shrink-0" fill="currentColor" />
                  <span className="text-sm text-stone-600">
                    <span className="font-semibold text-stone-800">{userCount.toLocaleString()}</span>
                    {' '}{userCount === 1 ? 'familia' : 'familias'} está guardando sus recuerdos para siempre
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="lg:w-[35%] w-full relative lg:pt-20">
            <ImageCarousel images={carouselImages} variant="vertical" speed="medium" />
            <ImageCarousel images={carouselImages} variant="horizontal" speed="fast" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
